import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDownload, FiFileText, FiImage, FiBookOpen, FiUsers, FiTarget, FiStar, FiExternalLink, FiMail, FiCheckCircle, FiShoppingCart, FiPackage } = FiIcons;

function Resources() {
  const downloadResources = [
    {
      icon: FiFileText,
      title: "Handbok för oss som jobbar",
      description: "En praktisk handbok för alla som arbetar med Okej-kulturen i sin organisation. Inkluderar It' OK to Poster.",
      format: "PDF",
      size: "434 kb",
      color: "from-blue-500 to-blue-600",
      downloadUrl: "https://sendfox.com/lp/1v4957 ",
      type: "free"
    },
    {
      icon: FiImage,
      title: "It's OK To Poster",
      description: "Tryckbar poster med alla \"Det är okej att...\"-påståenden för att sprida budskapet visuellt. ",
      format: "PDF",
      size: "575 kb ",
      color: "from-green-500 to-green-600",
      downloadUrl: "https://sendfox.com/lp/3e20jw ",
      type: "free"
    },
    {
      icon: FiBookOpen,
      title: "Gratis guide till Okej-kultur",
      description: "En guide som sammanfattar allt från okej.jonasjaani.se och ger dig konkreta verktyg och principer för att bygga en Okej-kultur",
      format: "PDF",
      size: "453 kb",
      color: "from-purple-500 to-purple-600",
      downloadUrl: "https://sendfox.com/lp/10lorv ",
      type: "free"
    },
    {
      icon: FiPackage,
      title: "Okej-Kultur Implementerings-paketet",
      description: "Bygg en bättre arbetskultur själva! Här är allt ni behöver i ett paket med guider, e-bok och material för interna workshops. En investering som betalar sig direkt.",
      format: "Digitalt paket",
      size: "Word, PDF",
      color: "from-orange-500 to-orange-600",
      downloadUrl: "https://www.keyfutureskills.com/product/okej-kultur-implementeringspaketet1/",
      type: "premium"
    },
    {
      icon: FiPackage,
      title: "Okej-Kultur Implementerings-paketet Plus",
      description: "Ett komplett paket med Okej-Kultur Implementerings-paketet samt en färdig plan och anpassningsbart utbildningsmaterial, så att ni kan komma igång direkt.",
      format: "Digitalt paket",
      size: "Word, PowerPoint, PDF",
      color: "from-indigo-500 to-purple-600",
      downloadUrl: "https://www.keyfutureskills.com/product/okej-kultur-implementeringspaketet2/",
      type: "premium"
    },
    {
      icon: FiUsers,
      title: "Skapa framgångsrika traineeprogram",
      description: "Guide för att bygga och implementera effektiva traineeprogram med Okej-kultur.",
      format: "Digital kurs",
      size: "ebok, video",
      color: "from-pink-500 to-rose-500",
      downloadUrl: "https://www.keyfutureskills.com/product/skapa-framgangsrika-traineeprogram/",
      type: "premium"
    }
  ];

  const additionalResources = [
    {
      icon: FiBookOpen,
      title: "Forskning & Bakgrund",
      description: "Vetenskapliga studier och forskning som ligger till grund för Okej-kulturen.",
      link: "/forskning"
    },
    {
      icon: FiStar,
      title: "Framgångshistorier",
      description: "Läs om organisationer som framgångsrikt implementerat Okej-kulturen.",
      link: "/framgang"
    },
    {
      icon: FiCheckCircle,
      title: "Utvärdering",
      description: "Utvärdera din organisations nuvarande kultur och upptäck förbättringsområden.",
      link: "/assessment"
    }
  ];

  const tipCategories = [
    {
      title: "För medarbetare",
      tips: [
        "Börja smått: Välj ett tillfälle i veckan att ställa en klargörande fråga",
        "Var ett exempel: Bekräfta och beröm kollegors mod att be om hjälp",
        "Reflektera över misstag: Dela öppet vad du lärt dig av misstag"
      ]
    },
    {
      title: "För ledare",
      tips: [
        "Visa sårbarhet: Erkänn dina egna kunskapsluckor och misstag",
        "Skapa trygga forum: Uppmana aktivt till frågor och ifrågasättande",
        "Främja lärande från misstag: Fokusera på lärande, inte skuldbeläggning"
      ]
    }
  ];

  const handleDownload = (resource) => {
    if (resource.type === "premium") {
      // For premium products, open the product page in a new tab
      window.open(resource.downloadUrl, '_blank');
    } else {
      // For free resources, download directly
      const link = document.createElement('a');
      link.href = resource.downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">Resurser</span> för att komma igång
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Allt du behöver för att implementera och sprida Okej-kulturen i din organisation. 
              Ladda ner, anpassa och börja din resa mot en mer human arbetsplats.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Där allt började</h2>
            <p style={{ color: 'rgb(75,85,99)' }} className="text-lg text-gray-600">En enkel skylt startade allt</p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759405989812-Ska%C3%8C%C2%88rmavbild%202025-10-02%20kl.%2011.36.31.png" 
                  alt="It's OK To poster från Government Digital Service" 
                  className="w-full rounded-lg shadow-md"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Government Digital Service - Pionjärerna</h3>
              <div className="space-y-4 text-gray-700">
                <p>
                  Denna ikoniska gula poster från Government Digital Service (GDS) blev symbolen för en ny typ av arbetsplats. 
                  Skapad av Sonia Turcotte och flera medarbetare, samlade den ihop alla de "Det är okej att..."-påståenden som definierar en human arbetskultur.
                </p>
                <p>
                  Från "It's ok to feel the way you feel" till "It's ok to love what you do" - varje rad representerar en grund för 
                  psykologisk trygghet och mänsklig värdighet på jobbet. Detta blev inspirationen för Okej-kulturen.
                </p>
                <p>
                  GDS visade världen att även inom myndigheter och tekniska organisationer kan mänsklighet och empati vara drivkrafter för innovation och framgång.
                </p>
              </div>
              <div className="mt-6 p-4 bg-yellow-100 rounded-lg">
                <p className="text-sm text-yellow-800 italic">
                  "Det är denna typ av mod att vara mänsklig som skapar de bästa teamen och de mest innovativa lösningarna." - Jonas Jaani
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Resources Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Gratis resurser</h2>
            <p className="text-lg text-gray-600">Börja din resa med dessa kostnadsfria material</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {downloadResources.filter(r => r.type === "free").map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${resource.color} flex items-center justify-center flex-shrink-0`}>
                    <SafeIcon icon={resource.icon} className="text-white text-2xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm">{resource.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        <span>{resource.format}</span> • <span>{resource.size}</span>
                      </div>
                      <button
                        onClick={() => handleDownload(resource)}
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 text-sm"
                      >
                        <SafeIcon icon={FiDownload} className="text-sm" />
                        <span>Ladda ner</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Resources Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Premium-paket</h2>
            <p className="text-lg text-gray-600">Kompletta lösningar för djupare implementering</p>
          </motion.div>
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            {downloadResources.filter(r => r.type === "premium").map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full">
                    PREMIUM
                  </span>
                </div>
                <div className="flex items-start space-x-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${resource.color} flex items-center justify-center flex-shrink-0`}>
                    <SafeIcon icon={resource.icon} className="text-white text-2xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm">{resource.description}</p>
                    <div className="text-xs text-gray-500 mb-4">
                      <span>{resource.format}</span> • <span>{resource.size}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(resource)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  <SafeIcon icon={FiShoppingCart} className="text-sm" />
                  <span>Mer information</span>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Practical Tips */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Praktiska tips för implementering</h2>
            <p className="text-lg text-gray-600">Konkreta råd för att börja bygga Okej-kulturen redan idag.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {tipCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">{category.title}</h3>
                <div className="space-y-4">
                  {category.tips.map((tip, tipIndex) => (
                    <div key={tipIndex} className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <SafeIcon icon={FiTarget} className="text-green-600 text-xs" />
                      </div>
                      <p className="text-gray-700 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ytterligare resurser</h2>
            <p className="text-lg text-gray-600">Fördjupa din förståelse och få inspiration från andra.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {additionalResources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
              >
                <Link to={resource.link} className="block text-center">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={resource.icon} className="text-white text-xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{resource.description}</p>
                  <div className="flex items-center justify-center text-blue-600 text-sm font-medium">
                    <span>Läs mer</span>
                    <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Behöver du hjälp med implementeringen?</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Kontakta Jonas Jaani för personlig rådgivning, workshops eller skräddarsydda lösningar för din organisation.
            </p>
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Jonas Jaani</h3>
              <p className="text-gray-600 mb-4">Skapare av Okej-kulturen</p>
              <a
                href="https://www.jonasjaani.se"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                Besök webbplats
                <SafeIcon icon={FiExternalLink} className="ml-2 text-sm" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Resources;